<template>
  <div style="margin: 0 20px 20px;">
    <div class="al-header-banner d-flex align-items-center justify-content-center flex-wrap" style="font-size: 20px; font-weight: bold; position: relative;">
      <div class="al-header-left">
        <span style="font-size: 24px;">Alert History</span>
      </div>

      <a href="#" class="white-link" v-on:click.prevent="shift_detail_graph_date(-1)"><</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="options" :placeholder="details_toDate"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_detail_graph_date(1)">></a>
      &nbsp;&nbsp;
      Types:&nbsp;<b-form-select v-model="alert_type" :options="type_options" style="font-size: 18px; font-weight: bold; width:290px"/>
      &nbsp;&nbsp;<b-button v-on:click.prevent="load_data()">Search</b-button>

      <div class="al-header-right">
        <b-button
          v-if="showEnableAlertsPackageButton"
          size="sm"
          variant="primary"
          class="mr-1"
          :to="{ name: 'alerts_dashboard', query: { upsell: '1' } }"
        >
          <b>Enable Alerts Package</b>
        </b-button>
        <b-button
          v-if="user.features[user.tenant] && user.features[user.tenant].alerts_enabled && (user.role === 'admin' || user.role === 'customer-admin' || user.role === 'tbn-admin' || user.role === 'dispatch' || user.role === 'manager')"
          size="sm"
          variant="primary"
          class="mr-1"
          :to="{ name: user.types[user.tenant] === 'TBN' ? 'group_message_sender_tbn' : 'group_message_sender' }"
          aria-label="Group Message"
          title="Group Message"
        >
          <i class="fa fa-bullhorn"></i>
        </b-button>
        <b-button size="sm" variant="success" @click="showEmailDeliveryInfoModal = true" aria-label="more info" title="more info"><i class="fa fa-info-circle"></i></b-button>
      </div>
    </div>

    <b-modal v-model="showEmailDeliveryInfoModal" title="Alert History Help" size="lg" ok-only ok-title="Close" scrollable>
      <div class="text-left">
        <h4>Using the Alert History page</h4>
        <ul>
          <li>Set the <b>date range</b> (or use the arrow buttons to shift day by day) and pick a <b>type</b> from the dropdown, then click <b>Search</b> to reload results.</li>
          <li>Use <b>Filter Results</b> to search the loaded rows by driver, vehicle, recipient, or message text without reloading from the server.</li>
          <li>Click the colored <b>type chips</b> below the search bar to filter by one or more alert types at once; click a selected chip again to remove it.</li>
          <li>Check <b>Problems Only</b> to show just the alerts with a failed SMS or a bounced/complaint email.</li>
          <li>Click <b>Download</b> to export the current filtered results to a spreadsheet.</li>
          <li>Click <b>+</b> on a row to expand the full message text and delivery details. :role[fleet-admin] can delete an individual alert record with the red <b>x</b>.</li>
        </ul>

        <hr/>

        <h4>Using alert feedback</h4>
        <p>
          You can flag a concern on each alert for your team to follow up on — support is
          notified automatically to help investigate. Look for the <b>flag</b> icon in the
          <b>Feedback</b> column on any alert row.
        </p>
        <ul>
          <li><b>Flag</b> — record a concern. Click the flag, describe the issue in the comment box that opens next to it, then submit — a comment is required. Click the flag again to review or update the concern.</li>
        </ul>
        <template v-if="feedback_audit_mode_enabled">
          <p>Feedback audit mode is enabled for your organization, which adds:</p>
          <ul>
            <li><b>Thumbs up</b> — mark the alert as correct or expected. Click again to clear your rating.</li>
            <li><b>Thumbs down</b> — mark the alert as incorrect or unexpected. Click again to clear.</li>
            <li>Use the <b>Feedback</b> filter chips above the table to show only alerts with a given rating. Hover each chip for a short description. Click a chip to filter; click the same chip again to show all rows.</li>
            <li>The <b>&times;</b> button on the Feedback filters clears every rating and comment for all alerts in the current date range (you will be asked to confirm).</li>
          </ul>
        </template>
        <p v-else class="text-muted" style="font-size: 13px;">
          Feedback audit mode is off for your organization, so only the flag is shown. A fleet admin can turn it on under Settings &gt; General.
        </p>

        <hr/>

        <h4>Email Delivery Status</h4>
        <div style="white-space: pre-line;" v-html="emailDeliveryInfoText"></div>
      </div>
    </b-modal>

    <div v-if="singleAlertFilter" class="al-single-alert-banner">
      Showing 1 alert record &nbsp;<a href="#" @click.prevent="clearSingleAlertFilter">Clear filter</a>
    </div>

    <div style="font-size: 20px; font-weight: bold; position: relative; margin: 30px" class="d-flex align-items-center justify-content-center">
      Filter Results&nbsp;<b-form-input style="width: 200px" v-model="alert_filter" placeholder=""/>
      <span v-if="base_options && base_options.length > 2">
        &nbsp;&nbsp;|&nbsp;&nbsp;<b-form-select v-model="selected_base" :options="base_options" style="width:150px"/>
      </span>
      <span v-if="driver_confirmation_enabled">
      &nbsp;&nbsp;Confirmed:&nbsp;<b-form-select v-model="confirmed_state" :options="confirmed_options" style="font-size: 20px; font-weight: bold; width:200px"/>
      </span>&nbsp;&nbsp;
      <b-form-checkbox
        v-model="needs_attention"
        class="ml-3"
      >
        Problems Only
      </b-form-checkbox>
    </div>

    <div v-if="!loading">
      <div class="row" style="margin: 10px 10px 10px 10px; font-weight: bold; font-size: 18px;">
        <div v-if="summary.total_cnt > 0" class="col" style="border: 1px solid grey;">Total: {{summary.total_cnt}}</div>
        <div v-if="items.length > 0" class="col" style="border: 1px solid grey;">Filtered: {{items.length}}</div>
        <div v-if="summary.email_cnt > 0" class="col" style="border: 1px solid grey;">Emails Sent: {{summary.email_cnt}}</div>
        <div v-if="summary.sms_cnt > 0" class="col" style="border: 1px solid grey;">SMS Sent: {{summary.sms_cnt}} (${{summary.sms_cost}})</div>
        <div v-if="summary.voice_cnt > 0" class="col" style="border: 1px solid grey;">Voice Sent: {{summary.voice_cnt}}</div>
        <div v-if="summary.sms_inbound_cnt > 0" class="col-2" style="border: 1px solid grey;">SMS Rcvd: {{summary.sms_inbound_cnt}} / ${{summary.sms_inbound_cost}}</div>
        <div>&nbsp;&nbsp;<b-button size="md" variant="success" @click="download">Download</b-button></div>
      </div>
    </div>

    <div class="al-chip-row">
      <span
        v-for="(value, key) in summary.type_unit_counts"
        :key="key"
        @click="filter_by_type(key)"
        class="al-chip al-chip-clickable"
        :class="{ 'al-chip-selected': selected_types.indexOf(key) > -1 }"
        :style="typeChipStyle(key)"
      >{{alertTypeLabel(key)}}: {{value.count}}</span>
    </div>

    <div v-if="feedback_audit_mode_enabled" class="actuals-filter-chips actuals-cm-filter-chips actuals-feedback-filter-row">
      <span class="actuals-filter-chip-group actuals-feedback-filter-group">
        <span class="actuals-filter-label" :title="feedbackFilterTooltips.group">Feedback</span>
        <span
          @click="filter_by_feedback('positive')"
          class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-positive"
          :class="{'actuals-chip-selected': selected_feedback == 'positive'}"
          :title="feedbackFilterTooltips.positive"
        ><i class="fa fa-check" aria-hidden="true"></i> {{ feedback_positive_cnt }}</span>
        <span
          @click="filter_by_feedback('negative')"
          class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-negative"
          :class="{'actuals-chip-selected': selected_feedback == 'negative'}"
          :title="feedbackFilterTooltips.negative"
        ><i class="fa fa-thumbs-down" aria-hidden="true"></i> {{ feedback_negative_cnt }}</span>
        <span
          @click="filter_by_feedback('concern')"
          class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-concern"
          :class="{'actuals-chip-selected': selected_feedback == 'concern'}"
          :title="feedbackFilterTooltips.concern"
        ><i class="fa fa-flag" aria-hidden="true"></i> {{ feedback_concern_cnt }}</span>
        <span
          @click="filter_by_feedback('none')"
          class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-none"
          :class="{'actuals-chip-selected': selected_feedback == 'none'}"
          :title="feedbackFilterTooltips.none"
        ><i class="fa fa-circle-o" aria-hidden="true"></i> {{ feedback_none_cnt }}</span>
        <span
          @click="confirm_clear_feedback_for_date_range"
          class="actuals-chip actuals-chip-clickable actuals-feedback-clear-btn"
          :class="{'actuals-chip-disabled': clearing_feedback}"
          :title="feedbackFilterTooltips.clear"
          role="button"
          aria-label="Clear all feedback for this date range"
        ><i class="fa fa-times" aria-hidden="true"></i></span>
      </span>
    </div>

    <div>
      <b-table :busy="loading" sticky-header="70vh" bordered :items="items" :fields="fields"
               :current-page="currentPage" :tbody-tr-class="get_row_color"
               :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="alert_type" slot-scope="row">
          <span :style="typeTextStyle(row.item.alert_type)">{{alertTypeLabel(row.item.alert_type)}}</span>
        </template>

        <template slot="booking_id" slot-scope="row">
          <router-link v-if="user.types[user.tenant] == 'CM'" style="font-weight: bold;" :to="{ path: '/actuals?booking_id=' + row.item.booking_hash }">{{ row.item.booking_id }}</router-link>
          <router-link v-else-if="user.types[user.tenant] == 'TBN'" style="font-weight: bold;" :to="{ path: '/actuals_tbn?booking_id=' + row.item.booking_hash }">{{ row.item.booking_id }}</router-link>
          <router-link v-else style="font-weight: bold;" :to="{ path: '/actuals_fasttrak?booking_id=' + row.item.booking_hash }">{{ row.item.booking_id }}</router-link>
        </template>

        <template slot="vehicle_id" slot-scope="row">
          <div>
            <span
              v-for="(id, idx) in (row.item.vehicle_id || '')
                .split(',')
                .map(s => s.trim())
                .filter(s => s && s.toLowerCase() !== 'none')"
              :key="id"
            >
              <router-link
                class="font-weight-bold"
                :to="{ path: '/vehicle_trip_history', query: { vehicleId: id } }"
              >{{ id }}</router-link><span
                v-if="
                  idx <
                  (
                    (row.item.vehicle_id || '')
                      .split(',')
                      .map(s => s.trim())
                      .filter(s => s && s.toLowerCase() !== 'none')
                  ).length - 1
                "
              >, </span>
            </span>
          </div>
        </template>

        <template slot="processed_time" slot-scope="row">
          {{row.item.processed_time | formatDateTimeSmall}}
          <span v-if="row.item.movement_vehicle_id.includes('reprocess')">(reprocessed)</span>
        </template>

        <template slot="confirmed_time" slot-scope="row">
          {{row.item.confirmed_time | formatDateTimeSmall}}
        </template>

        <template slot="start_datetime" slot-scope="row">
          {{row.item.start_datetime | formatDateTimeSmall}}
        </template>

        <template slot="message" slot-scope="row">
          <div v-if="row.item.voice_msg"><b>voice text:</b> {{row.item.voice_msg | trim(300)}}<hr/><b>sms text:</b></div>
          <div v-if="row.item.email_subject"><b>email subject:</b> {{ row.item.email_subject }}</div>
          <div v-if="row.item.email_message && !row.item.voice_msg && !row.item.email_subject && !row.item.message">see details...</div>
          <div v-if="row.item.message && row.item.message.length < 400" style="text-align: left; font-family: monospace; white-space: normal !important;" v-html="row.item.message.replaceAll('\n','<br/>')"/>
          <div v-else-if="row.item.message" @click="row.toggleDetails" style="text-align: left; font-family: monospace; white-space: normal !important;" v-html="row.item.message.substring(0,400).replaceAll('\n','<br/>') + '<br/>...'"/>
        </template>

        <template slot="email" slot-scope="row">
          <template v-if="row.item.email">
            <div style="text-align: left;"  v-for="val in row.item.email.split(',')">
              {{val}}
            </div>
          </template>
        </template>

        <template slot="sms" slot-scope="row">
          <div v-if="row.item.voice_number"><b>voice #:</b> {{row.item.voice_number}}<hr/><b>sms #:</b></div>
          <div v-if="row.item.sms" class="col" :style="row.item.sms.split(',').length > 3 ? 'overflow-y:scroll; max-height: 100px; border: 0px': 'border: 0px'">
            <div v-for="val in row.item.sms.split(',')">
              {{val}}
            </div>
          </div>
        </template>

        <template slot="provider_response" slot-scope="row">
          <div style="font-size:14px; line-height:1.4;">
            <div
              v-if="row.item.provider_response && !row.item.provider_response.includes('Message has been accepted')"
              style="font-weight:bold; color:darkred;"
            >
              sms: {{ row.item.provider_response }}
              <span v-if="row.item.phone_carrier"><br/>[{{ row.item.phone_carrier }}]</span>
            </div>

            <div
              v-else-if="row.item.mock_send"
              style="font-weight:bold; color:gray;"
            >
              test: no real message sent
            </div>

            <div
              v-else-if="row.item.sms_msg_id"
              style="font-weight:bold;"
            >
              <span style="color:green">sms: success</span>
              <span v-if="row.item.retry_attempted" style="color:darkgoldenrod">
                (retried)
              </span>
            </div>

            <div
              v-if="getLatestEmailStatus(row.item) && ['bounce','complaint'].includes(getLatestEmailStatus(row.item).status.toLowerCase())"
              style="font-weight:bold; color:darkred; margin-bottom:6px;"
            >
              <div>
                email: {{ decodeEmailStatus(getLatestEmailStatus(row.item).status) }}
              </div>
              <div><small>{{ getLatestEmailStatus(row.item).datetime | formatDateTimeSmall }}</small></div>
            </div>

            <div
              v-else-if="getLatestEmailStatus(row.item)"
              style="margin-bottom:6px;"
            >
              <div>
                <strong style="color:green">
                  email: {{ decodeEmailStatus(getLatestEmailStatus(row.item).status) }}
                </strong>
              </div>
              <div><small>{{ getLatestEmailStatus(row.item).datetime | formatDateTimeSmall }}</small></div>
            </div>
          </div>
        </template>

        <template slot="sms_drivers" slot-scope="row">
          <div v-if="row.item.sms_drivers" class="col" :style="row.item.sms_drivers.split(',').length > 3 ? 'overflow-y:scroll; max-height: 100px; border: 0px': 'border: 0px'">
            <div v-for="val in row.item.sms_drivers.split(',')">
              {{val}}
            </div>
          </div>
        </template>

        <template slot="sms_leaders" slot-scope="row">
          <div v-if="row.item.sms_leaders" class="col" :style="row.item.sms_leaders.split(',').length > 3 ? 'overflow-y:scroll; max-height: 100px; border: 0px': 'border: 0px'">
            <div v-for="val in row.item.sms_leaders.split(',')">
              {{val}}
            </div>
          </div>
        </template>

        <template slot="finish_datetime" slot-scope="row">
          {{row.item.finish_datetime | formatDateTimeSmall}}
        </template>

        <template slot="details" slot-scope="row">
          <b-button size="sm" @click="row.toggleDetails">
            {{ row.detailsShowing ? '-' : '+' }}
          </b-button>
        </template>

        <template slot="HEAD_feedback">
          <i v-if="!feedback_audit_mode_enabled" class="fa fa-flag" style="color: #dc3545;" title="Feedback" aria-hidden="true"></i>
          <span v-else>Feedback</span>
        </template>

        <template slot="feedback" slot-scope="row">
          <alert-feedback-controls :item="row.item" :tenant="user.tenant" :audit-mode="feedback_audit_mode_enabled" @showAlert="(msg, variant, ms) => $emit('showAlert', msg, variant, ms)" />
        </template>

        <template slot="reprocess" slot-scope="row">
          <span v-if="row.item.alert_type == 'CustomerTripNotification'" size="sm" @click="reprocess_alert(row.item)"><span style="font-size: 35px;">&#8635;</span></span>
        </template>

        <template slot="delete" slot-scope="row">
          <span
            v-if="isEffectiveAdmin"
            title="Delete alert record"
            style="font-size: 22px; font-weight: bold; color: #b30000; cursor: pointer;"
            @click="delete_alert_record(row.item)"
          >x</span>
        </template>

        <template slot="row-details" slot-scope="row">
          <b-card style="text-align: left;">
            <span v-if="row.item.email">
              <b>email recipients:</b> {{row.item.email}}<br/>
            </span>
            <span v-if="row.item.email_subject">
              <br/><b>email subject:</b> {{ row.item.email_subject }}<br/>
            </span>
            <span v-if="row.item.email_message">
              <br/><b>email message:</b><br/><br/><span style="text-align: left; font-family: monospace; white-space: normal !important;" v-html="row.item.email_message"/>
            </span>

            <span class="recipients-list" v-if="row.item.sms_all_recipients">
              <b>SMS recipients:</b>{{row.item.sms_all_recipients}}<br/>
            </span>
            <br/>
            <span v-if="row.item.message.indexOf('<html') != -1">
              <b>SMS message:</b><div style="text-align: left; font-family: monospace; white-space: normal !important;" v-html="row.item.message"/>
            </span>
            <span v-else-if="row.item.message">
              <b>SMS message:</b>
              <span style='min-width: 2100px; display: flex; flex-wrap: wrap'>
                <pre>{{row.item.message}}</pre>
              </span>
            </span>

            <div v-if="row.item.prev_message">
              <b>previous message:</b><br/>
              <pre style='min-width: 2100px; display: flex; flex-wrap: wrap'>{{row.item.prev_message}}</pre>
            </div>
          </b-card>
        </template>
      </b-table>
      <b-row>
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>

import {api_url, clear_alert_feedback_for_date_range, delete_alert, get_alerts, reprocess_alerts_for_booking} from '../networking/apis'
import {mapState} from "vuex";
import moment from 'moment'
import {justDate} from "@/helpers/dates";
import axios from 'axios';
import { hasAlertsAccess } from '../utils/alerts_access'
import { hasVsmAccess } from '../utils/vsm_access'
import alert_feedback_controls from '@/components/alert_feedback_controls'
import { isFeedbackAuditModeEnabled } from '@/helpers/feedback_audit_mode'
import { countAlertFeedback, matchesAlertFeedbackFilter, ALERT_FEEDBACK_FILTER_TOOLTIPS } from '@/helpers/alert_feedback_filter'

// alert type is an open-ended set, so colors are assigned by hashing the value
// into this fixed palette rather than a static per-value map. solid colors +
// white text, matching the delivery_failures.vue chip style. hues are spread
// evenly around the color wheel (only one indigo/purple each) so hashed
// assignments don't cluster into near-duplicate shades.
const TYPE_CHIP_COLOR_PALETTE = [
  '#dc3545', '#fd7e14', '#b8860b', '#198754',
  '#0d9488', '#0891b2', '#0d6efd', '#6610f2',
  '#9c27b0', '#d63384', '#8b4513', '#495057',
]

export default {
  name: 'alert_list',
  components: {
    'alert-feedback-controls': alert_feedback_controls,
  },

  data() {
    return {
      msg: 'alerts',
      showEmailDeliveryInfoModal: false,
      emailDeliveryInfoText: `The different email statuses you're seeing represent the progression of an email through the delivery process:

1. <strong>Sent</strong> - This means our system successfully sent the email out. At this point, the email has left our servers and is on its way to the recipient.

2. <strong>Delivered</strong> - This means the recipient's mail server confirmed it received the email. The email has successfully arrived at the destination inbox.

3. <strong>Opened</strong> - This means the recipient actually opened the email. We detect this through a tiny invisible tracking pixel in the email - when someone opens the email and their email client loads images, we get notified.

So the typical happy path is: <strong>Sent → Delivered → Opened</strong>

<strong>Why you might see only "Sent" vs "Delivered":</strong>

"Sent" alone could mean we're still waiting for the delivery confirmation (usually happens within seconds to minutes)

Some mail servers don't send delivery confirmations back

<strong>Why "Opened" doesn't always appear:</strong>

Not all email clients load images by default (Outlook, for example, often blocks them)

If someone reads the email in a preview pane without images enabled, we won't see the "Opened" event."`,
      alert_filter: '',
      needs_attention: false,
      sms_stats_enabled: true,
      driver_confirmation_enabled: true,
      items: [],
      items_full: [],
      summary: [],
      base_options: [],
      selected_base: '',
      loading: true,
      details_fromDate: justDate(moment().add(-1, 'days')),
      details_toDate: justDate(moment()),
      alert_type: '',
      selected_types: [],
      singleAlertFilter: null,
      selected_feedback: null,
      clearing_feedback: false,
      items_for_feedback_counts: [],
      feedbackFilterTooltips: ALERT_FEEDBACK_FILTER_TOOLTIPS,
      confirmed_state:'',
      totalRows: 1,
      currentPage: 1,
      perPage: 100,
      pages: [],
      type_options: [
        { value: '', text: 'All Types' },
        { value: 'LateAlert', text: 'Late Alerts' },
        { value: 'DriverTripNotification', text: 'Driver Trip Notifications' },
        { value: 'CustomerTripNotification', text: 'Customer Trip Notifications' },
        { value: 'CustomerPostTripNotification', text: 'Customer Post Trip Notifications' },
        { value: 'PassengerTripNotification', text: 'Passenger Trip Notifications' },
        { value: 'MissingDVIR', text: 'Missing DVIR Alert' },
        { value: 'MissingDriverConfirmations', text: 'Missing Driver Confirmations' },
        { value: 'PostTripNotification', text: 'Post Trip Notifications' },
        { value: 'VehicleActivityAlert', text: 'Vehicle Activity Alert' },
        { value: 'VehicleInactivityAlert', text: 'Vehicle Inactivity Alert' },
        { value: 'RelayNotification', text: 'Relay Notifications' },
        { value: 'DriverHOSAlert', text: 'HOS Duration Alert' },
        { value: 'UnplannedVehicleActivityAlert', text: 'Unplanned Vehicle Activity' },
        { value: 'CustomerSurvey', text: 'Customer Survey' },
        { value: 'GroupMessage', text: 'Group Message' },
        { value: 'VehicleStatusReceived', text: 'Vehicle Status' },
        { value: 'EmailReceived', text: 'Email Received' },
        { value: 'SMSReceived', text: 'SMS Received' },
        { value: 'SMSResponse', text: 'SMS Response' },
        { value: 'GatewayDisconnectAlert', text: 'Gateway Disconnect Alert' },
        { value: 'BookingStateAlert', text: 'Booking State Alert' },
      ],
      confirmed_options: [
        { value: '', text: 'All' },
        { value: 'unconfirmed', text: 'Unconfirmed' },
        { value: 'confirmed', text: 'Confirmed' },
      ],
      fields_all: [],
      fields: [
        { key: 'details', label: '', sortable: false, thStyle: { width: "20px" }},
        //{ key: 'booking_hash', label: 'Booking Ref', sortable: true, thStyle: { width: "110px" }},
        { key: 'booking_id', label: 'Booking ID', sortable: true, thStyle: { width: "110px" }},
        { key: 'vehicle_id', label: 'Vehicle', sortable: true, thStyle: { width: "110px" }},
        { key: 'start_base', label: 'Base', sortable: true, thStyle: { width: "110px" }},
        { key: 'alert_type', label: 'Alert Type', sortable: true, thStyle: { width: "120px" }},
        { key: 'processed_time', label:'Alert Time', sortable: true, thStyle: { width: "110px" }},
        { key: 'provider_response', label:'Delivery Status', sortable: true, thStyle: { width: "120px" }},
        { key: 'confirmed_time', label:'Conf Time', sortable: true, thStyle: { width: "110px" }},
        // { key: 'sms_recipients', label:'SMS #', sortable: true, thStyle: { width: "110px" }},
        // { key: 'sms_size', label:'SMS Size', sortable: true},
        // { key: 'sms_units', label:'SMS Units', sortable: true},
        { key: 'sms_name', label:'Recipient', sortable: true, thStyle: { width: "110px" }},
        { key: 'sms_drivers', label:'Driver #', sortable: true, thStyle: { width: "120px" }},
        { key: 'sms_leaders', label:'Customer #', sortable: true, thStyle: { width: "120px" }},
        { key: 'sms', label:'Other #', sortable: true, thStyle: { width: "120px" }},
        { key: 'email', label: 'Email Recipients', sortable: true, thStyle: { width: "120px" }},
        { key: 'message', label: 'Message', sortable: true, thStyle: { width: "400px" }},
        { key: 'feedback', label: 'Feedback', sortable: false, thStyle: { width: "90px" }},
        { key: 'reprocess', label: '', sortable: false, thStyle: { width: "20px" }},
        { key: 'delete', label: '', sortable: false, thStyle: { width: "20px" }},
      ]
      ,
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(360, 'days'),
        maxDate: moment(),
        keepOpen: false,
        showClear: true,
        showClose: true,
        toolbarPlacement: 'top',
      },

    }
  },

  async created() {
    this.fields_all = this.fields
    this.applyDeepLinkQuery()
    this.check_features()
  },
  methods: {
    // deep-link from feedback review: jump straight to the single alert
    // record that generated a feedback rating, rather than a free-text search.
    applyDeepLinkQuery() {
      const query = this.$route.query || {}
      if (!query.movement_vehicle_id)
        return
      this.singleAlertFilter = String(query.movement_vehicle_id)
      if (query.alert_type)
        this.alert_type = query.alert_type
      if (query.date) {
        const target = moment(query.date, 'YYYY-MM-DD')
        if (target.isValid()) {
          // pad a day on either side to survive timezone rounding between
          // when the alert was recorded and how the date filter is applied
          this.details_fromDate = target.clone().subtract(1, 'days').format('MM-DD-YYYY')
          this.details_toDate = target.clone().add(1, 'days').format('MM-DD-YYYY')
        }
      }
    },
    clearSingleAlertFilter() {
      this.singleAlertFilter = null
      const query = Object.assign({}, this.$route.query)
      delete query.movement_vehicle_id
      delete query.date
      delete query.alert_type
      this.$router.replace({ query }).catch(() => {})
      this.filter_results()
    },
    async check_features() {
      this.check_cols()
      this.load_data()
    },
    filter_by_type(type) {
      let idx = this.selected_types.indexOf(type)
      if (idx > -1) {
        this.selected_types.splice(idx, 1);
      } else {
        this.selected_types.push(type)
      }
    },
    // falls back to the raw value (with PascalCase/hyphens split into words)
    // for types not covered by type_options, e.g. "MissingDVIR-PRE" -> "Missing DVIR PRE"
    alertTypeLabel(type) {
      const found = this.type_options.find(o => o.value === type)
      if (found) return found.text
      return (type || '')
        .replace(/-/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    },
    hashString(str) {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) | 0
      }
      return Math.abs(hash)
    },
    typeChipStyle(type) {
      const bg = TYPE_CHIP_COLOR_PALETTE[this.hashString(type) % TYPE_CHIP_COLOR_PALETTE.length]
      return { backgroundColor: bg, borderColor: bg, color: '#fff' }
    },
    // same per-type color as the filter chip, but as bold text for the results table
    typeTextStyle(type) {
      const color = TYPE_CHIP_COLOR_PALETTE[this.hashString(type) % TYPE_CHIP_COLOR_PALETTE.length]
      return { color, fontWeight: 'bold' }
    },
    async check_cols() {
      this.sms_stats_enabled = this.isEffectiveAdmin
      this.driver_confirmation_enabled = this.user.features[this.user.tenant].driver_trip_notifications_confirmation_enabled
      this.relay_notifications_enabled = this.user.features[this.user.tenant].relay_notifications_enabled
      this.driver_surveys_enabled = this.user.features[this.user.tenant].driver_surveys_enabled
      this.group_message_sender_enabled = this.user.features[this.user.tenant].group_message_sender_enabled
      this.fields = this.fields_all
      if (!this.driver_confirmation_enabled)
        this.fields = this.fields.filter(item => item.key != 'confirmed_time')

      if (!this.sms_stats_enabled)
        this.fields = this.fields.filter(item => (item.key != 'booking_hash' && item.key != 'sms_size' && item.key != 'sms_recipients' && item.key != 'sms_units'))

      if (!this.isEffectiveAdmin)
        this.fields = this.fields.filter(item => item.key != 'delete')

      if (this.alert_type && (this.alert_type == 'SMSReceived' || this.alert_type == 'SMSResponse'))
        this.fields = this.fields.filter(item => (item.key != 'booking_id' && item.key != 'booking_hash' && item.key != 'vehicle_id' && item.key != 'sms_drivers' && item.key != 'sms_leaders'))
      else if (this.alert_type && (this.alert_type == 'GroupMessage'))
        this.fields = this.fields.filter(item => (item.key != 'booking_id' && item.key != 'booking_hash' && item.key != 'vehicle_id' && item.key != 'sms_drivers' && item.key != 'sms_leaders' && item.key != 'sms_name'))
      else if (this.alert_type && (this.alert_type == 'DriverHOSAlert'))
        this.fields = this.fields.filter(item => (item.key != 'booking_id' && item.key != 'booking_hash' && item.key != 'vehicle_id' && item.key != 'sms_leaders'))
    },
    async load_data() {
      this.loading = true
      try {
        const {value: { data = [] } } = await get_alerts(this.user.tenant, this.alert_type, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
        this.summary = data.summary
        moment.tz.setDefault(this.summary.timezone)
        this.items = data.alerts
        this.items_full = data.alerts
        this.base_options = data.summary.bases
        // this.type_options = data.summary.alert_types
        this.filter_results()
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }
      this.loading = false
    },
    async reprocess_alert(item) {
      if (confirm("Are you sure you want to reprocess and resend notifications for this alert?")) {
        reprocess_alerts_for_booking(this.user.tenant, item.alert_type, item.booking_hash)
        this.$emit('showAlert', 'reprocessing alert', 'info', 5000)
      }
    },
    async delete_alert_record(item) {
      if (!this.isEffectiveAdmin) {
        return
      }
      if (!item || !item.movement_vehicle_id) {
        this.$emit('showAlert', 'unable to delete this alert record (missing id)', 'danger', 5000)
        return
      }
      const bookingId = item.booking_id || 'N/A'
      const alertType = item.alert_type || 'Unknown'
      const confirmed = confirm(
        `Delete this alert record?\n\nBooking: ${bookingId}\nType: ${alertType}\n\nThis cannot be undone.`
      )
      if (!confirmed) return
      try {
        await delete_alert(this.user.tenant, item.movement_vehicle_id)
        this.$emit('showAlert', 'alert record deleted', 'success', 5000)
        await this.load_data()
      } catch (e) {
        this.$emit('showAlert', 'failed to delete alert record', 'danger', 5000)
      }
    },
    async download(){
      this.$emit('showAlert', 'processing download request', 'info', 5000)
      axios.defaults.headers.common.Accept = {}
      axios.post(
          `${api_url}bookings/alerts/download`,
          {
            tenant: this.user.tenant,
            start_date: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            end_date: moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            alert_type: this.alert_type,
            alert_filter: this.alert_filter,
            start_base: this.selected_base
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwt')}`,
              Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
      ).then((response) => {
        axios.defaults.headers.common.Accept = 'application/json'
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
            'download',
            `alerts-${this.user.tenant}-${this.details_fromDate}.csv`
        )
        document.body.appendChild(link)
        link.click()
        this.$emit('showAlert', 'download complete', 'success', 5000)
      }).catch(e => {
        axios.defaults.headers.common.Accept = 'application/json'
        this.$emit('showAlert', 'alert data could not be downloaded.', 'danger')
      })
    },
    async shift_detail_graph_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
    },
    toggle_div(item,field) {
      item[field] = !item[field];
      this.$forceUpdate();
    },
    get_row_color(item, type) {
      if (!item || type !== 'row') return
      return item.status
    },
    filter_results() {
      this.items = this.items_full
      if (this.singleAlertFilter) {
        this.items = this.items.filter(item => String(item.movement_vehicle_id) === this.singleAlertFilter)
        this.items_for_feedback_counts = this.items
        return
      }
      if (this.selected_types.length > 0)
        this.items = this.items.filter(item => (this.selected_types.indexOf(item.alert_type) != -1))
      if (this.alert_type != '')
        this.items = this.items.filter(item => (this.contains(item.alert_type,this.alert_type)))
      if (this.confirmed_state != '')
        if (this.confirmed_state != 'confirmed')
          this.items = this.items.filter(item => (item.confirmed_time == null))
        else if (this.confirmed_state != 'unconfirmed')
          this.items = this.items.filter(item => (item.confirmed_time != null))
      if (this.alert_filter != '') {
        const val = this.alert_filter
        this.items = this.items.filter(item => (this.contains(item.provider_response, val) || this.contains(item.message, val) || this.contains(item.start_base, val) || this.contains(item.booking_id, val) || this.contains(item.email, val) || this.contains(item.sms_leaders, val) || this.contains(item.sms_drivers, val) || this.contains(item.sms, val) || this.contains(item.sms_name, val) || this.contains(item.vehicle_id, val)))
      }
      if (this.selected_base) {
        this.items = this.items.filter(item => this.contains(item.start_base,this.selected_base))
      }
      if (this.needs_attention) {
        this.items = this.items.filter(item => {
          const emailStatus = this.getLatestEmailStatus(item);
          const hasEmailIssue =
            emailStatus &&
            ['bounce','complaint'].includes(emailStatus.status.toLowerCase());
          const hasSmsIssue =
            item.provider_response &&
            !item.provider_response.includes('Message has been accepted');
          return hasEmailIssue || hasSmsIssue;
        });
      }
      // feedback chip counts reflect every other active filter, so snapshot
      // here before the feedback filter itself narrows this.items further
      this.items_for_feedback_counts = this.items
      if (this.selected_feedback != null && this.feedback_audit_mode_enabled) {
        this.items = this.items.filter(item => matchesAlertFeedbackFilter(item, this.selected_feedback))
      }
    },
    filter_by_feedback(feedback) {
      if (feedback == null || this.selected_feedback === feedback) {
        this.selected_feedback = null
      } else {
        this.selected_feedback = feedback
      }
      this.currentPage = 1
      this.filter_results()
    },
    async confirm_clear_feedback_for_date_range() {
      if (this.clearing_feedback || !this.feedback_audit_mode_enabled)
        return
      const fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const toDate = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const ratedCount = this.feedback_positive_cnt + this.feedback_negative_cnt + this.feedback_concern_cnt
      const rangeLabel = `${this.details_fromDate} to ${this.details_toDate}`
      const msg = ratedCount > 0
        ? `Delete feedback on ${ratedCount} alerts from ${rangeLabel}?`
        : `Delete all alert feedback from ${rangeLabel}?`
      if (!confirm(`${msg} This cannot be undone from the Alert History page.`))
        return
      this.clearing_feedback = true
      try {
        const {value: {data = {}}} = await clear_alert_feedback_for_date_range(this.user.tenant, fromDate, toDate)
        const deleted = data.deleted_count || 0
        this.selected_feedback = null
        this.clear_feedback_from_loaded_rows()
        this.$emit('showAlert', `Cleared feedback on ${deleted} alerts`, 'success', 4000)
        if (deleted > 0)
          await this.load_data()
      } catch (e) {
        console.warn('Failed to clear alert feedback:', e)
        this.$emit('showAlert', 'error clearing feedback, try again or contact support', 'danger', 5000)
      } finally {
        this.clearing_feedback = false
      }
    },
    clear_feedback_from_loaded_rows() {
      for (const item of this.items_full || []) {
        this.$delete(item, 'feedback')
        this.$delete(item, 'feedback_comment')
      }
    },
    contains(val1, val2) {
      if (val1 != null) {
        return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
      }
      return false;
    },

    getLatestEmailStatus(item) {
      if (!item.email_msg_status || !item.email_msg_status.length) return null;
      return item.email_msg_status.reduce((a, b) =>
        new Date(a.datetime) > new Date(b.datetime) ? a : b
      );
    },

    decodeEmailStatus(rawStatus) {
      if (!rawStatus) return ''
      const map = {
        send:      'sent',
        delivery:  'delivered',
        bounce:    'bounced',
        complaint: 'filed complaint',
        open:      'opened',
        click:     'clicked link',
      }
      return map[rawStatus.toLowerCase()] || rawStatus
    },
  },
  computed:{
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    isEffectiveAdmin() {
      return this.user.role === 'admin' && this.viewAs !== 'non-admin'
    },
    tenantFeatures() {
      return (this.user.features && this.user.features[this.user.tenant]) || {}
    },
    showEnableAlertsPackageButton() {
      return hasVsmAccess(this.tenantFeatures) && !hasAlertsAccess(this.tenantFeatures)
    },
    feedback_audit_mode_enabled() {
      return isFeedbackAuditModeEnabled(this.user)
    },
    feedback_positive_cnt() {
      return countAlertFeedback(this.items_for_feedback_counts, 'positive')
    },
    feedback_negative_cnt() {
      return countAlertFeedback(this.items_for_feedback_counts, 'negative')
    },
    feedback_concern_cnt() {
      return countAlertFeedback(this.items_for_feedback_counts, 'concern')
    },
    feedback_none_cnt() {
      return countAlertFeedback(this.items_for_feedback_counts, 'none')
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.check_features()
      }
    },
    alert_type(target_from_date) {
      this.check_cols()
      this.filter_results();
    },
    confirmed_state(target_from_date) {
      this.filter_results()
    },
    alert_filter() {
      this.filter_results()
    },
    needs_attention() {
      this.filter_results();
    },
    selected_base() {
      this.filter_results()
    },
    selected_types() {
      this.filter_results()
    },
    details_fromDate(target_from_date) {
      this.details_fromDate = target_from_date
      if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
        this.details_toDate = this.details_fromDate
    }
  }
}
</script>

<style>
.al-header-banner {
  width: 100%;
  margin-bottom: 25px;
  padding: 16px 20px;
  background: linear-gradient(180deg, #f4f8fc 0%, #e8f0f8 100%);
  border: 1px solid #d3e2f0;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.al-header-left {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
}

.al-header-right {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
}

/* below this width the absolutely-positioned title/buttons would overlap the
   centered date-nav content as it wraps, so stack everything into its own
   full-width line instead */
@media (max-width: 768px) {
  .al-header-left,
  .al-header-right {
    position: static;
    transform: none;
    width: 100%;
    justify-content: center;
    margin: 6px 0;
  }
}

.al-single-alert-banner {
  margin: 10px 20px;
  padding: 8px 14px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
}

/* chip look matches delivery_failures.vue's .df-chip / .df-chip-clickable */
.al-chip-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 8px;
  margin: 16px 10px;
  font-size: 14px;
  font-weight: bold;
}

.al-chip {
  display: inline-block;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 3px 10px;
  white-space: nowrap;
  line-height: 1.3;
}

.al-chip-clickable {
  cursor: pointer;
}

.al-chip-clickable:hover {
  filter: brightness(0.95);
}

.al-chip-selected {
  border: 2px solid #dc3545 !important;
  box-shadow: 0 0 0 1px rgba(220, 53, 69, 0.2);
  padding: 2px 9px;
}

.error { background-color: rgba(255,0,0,0.25)}
.success { background-color: hsla(120,100%,75%,0.3)}
.unprocessed { background-color: hsla(200,40%,80%,0.3) }
.cancelled { background-color: hsla(290,60%,70%,0.3) }

.error td { background-color: rgba(255,0,0,0.25)}
.success td {background-color: hsla(120,100%,75%,0.3)}
.unprocessed td { background-color: hsla(200,40%,80%,0.3) }
.cancelled td { background-color: hsla(290,60%,70%,0.3) }


div.row:nth-child(even) {
  background-color: #f2f2f2;
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.header {
  font-weight: bold;
}

div.col {
  border: 1px solid grey;
}

.table td {
  padding: .1rem !important;
}

.mvmt-table td {
  vertical-align: middle !important;
}

div.wrap {
  border: 1px solid grey;
  flex-wrap: wrap;
  display: flex;
  inline-size: 150px;
  /* overflow-wrap: break-word; */
}

recipients-list {
  white-space: break-spaces !important;
}

</style>

